import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Activity, TrendingUp, Users, Eye, Filter, RefreshCw, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import axios from 'axios';
import { getToken } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ACTION_COLORS = {
  login:    'bg-blue-100 text-blue-800',
  create:   'bg-green-100 text-green-800',
  update:   'bg-yellow-100 text-yellow-800',
  delete:   'bg-red-100 text-red-800',
  suspend:  'bg-orange-100 text-orange-800',
  activate: 'bg-teal-100 text-teal-800',
};

function formatDate(dateString) {
  if (!dateString) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  }).format(new Date(dateString));
}

export default function ActivityLogs() {
  const { user } = useAuth();
  const [logs, setLogs]       = useState([]);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const [filters, setFilters] = useState({
    user_email: '',
    action: '',
    resource_type: '',
    customer_id: '',
    date_from: '',
    date_to: '',
  });

  const [pagination, setPagination] = useState({ limit: 50, skip: 0, total: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/activity-stats`, { headers: getAuthHeader() });
      setStats(r.data);
    } catch {}
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: pagination.limit, skip: pagination.skip });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await axios.get(`${API_URL}/admin/activity-logs?${params}`, { headers: getAuthHeader() });
      setLogs(r.data.logs);
      setPagination(prev => ({ ...prev, total: r.data.total }));
    } catch {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.skip, filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const clearFilters = () => {
    setFilters({ user_email: '', action: '', resource_type: '', customer_id: '', date_from: '', date_to: '' });
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  if (user?.role !== 'super_admin') {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold font-heading">Activity & Audit Logs</h1>
            <p className="text-muted-foreground mt-1">Monitor all platform activity and user actions</p>
          </div>
          <Button onClick={() => { fetchStats(); fetchLogs(); toast.success('Refreshed'); }} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />Refresh
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Logs', value: stats.total_logs?.toLocaleString(), sub: 'All time', Icon: Activity },
              { label: 'Last 24 Hours', value: stats.recent_logs_24h?.toLocaleString(), sub: 'Recent activity', Icon: TrendingUp },
              { label: 'Top Action', value: stats.top_actions?.[0]?.action || 'N/A', sub: `${stats.top_actions?.[0]?.count || 0}× (7 days)`, Icon: Eye },
              { label: 'Active Customers', value: stats.top_customers?.length, sub: 'Last 7 days', Icon: Users },
            ].map(({ label, value, sub, Icon }) => (
              <Card key={label}>
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{label}</CardTitle>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold capitalize">{value}</div>
                  <p className="text-xs text-muted-foreground mt-1">{sub}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />Filters
              </CardTitle>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />Clear all
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs mb-1 block">User Email</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="tamfawcett@gmail.com"
                    value={filters.user_email}
                    onChange={e => handleFilterChange('user_email', e.target.value)}
                    data-testid="filter-email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Action</Label>
                <Input
                  placeholder="login, create, update, delete…"
                  value={filters.action}
                  onChange={e => handleFilterChange('action', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Resource Type</Label>
                <Input
                  placeholder="tile, user, customer…"
                  value={filters.resource_type}
                  onChange={e => handleFilterChange('resource_type', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Date From</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={e => handleFilterChange('date_from', e.target.value)}
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Date To</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={e => handleFilterChange('date_to', e.target.value)}
                  data-testid="filter-date-to"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Customer ID</Label>
                <Input
                  placeholder="Filter by customer ID"
                  value={filters.customer_id}
                  onChange={e => handleFilterChange('customer_id', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Logs</CardTitle>
            <CardDescription>
              Showing {logs.length} of {pagination.total} logs
              {hasActiveFilters && ' (filtered)'}
              {' — '}click any row to see full details
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading logs…</div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No activity logs match your filters</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-3 px-4 font-medium w-6"></th>
                      <th className="text-left py-3 px-4 font-medium whitespace-nowrap">Timestamp</th>
                      <th className="text-left py-3 px-4 font-medium">User</th>
                      <th className="text-left py-3 px-4 font-medium">Action</th>
                      <th className="text-left py-3 px-4 font-medium">Resource</th>
                      <th className="text-left py-3 px-4 font-medium">Customer</th>
                      <th className="text-left py-3 px-4 font-medium min-w-[280px]">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => {
                      const isExpanded = expandedId === log.id;
                      return (
                        <React.Fragment key={log.id}>
                          <tr
                            className="border-b hover:bg-muted/50 cursor-pointer"
                            onClick={() => setExpandedId(isExpanded ? null : log.id)}
                          >
                            <td className="py-3 px-4 text-muted-foreground">
                              {isExpanded
                                ? <ChevronDown className="w-4 h-4" />
                                : <ChevronRight className="w-4 h-4" />}
                            </td>
                            <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                              {formatDate(log.created_at)}
                            </td>
                            <td className="py-3 px-4">
                              <div className="font-medium">{log.user_name}</div>
                              <div className="text-xs text-muted-foreground">{log.user_email}</div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800'}`}>
                                {log.action}
                              </span>
                            </td>
                            <td className="py-3 px-4 capitalize">{log.resource_type || '—'}</td>
                            <td className="py-3 px-4">{log.customer_name || <span className="text-muted-foreground">—</span>}</td>
                            <td className="py-3 px-4 text-muted-foreground">
                              <span className="line-clamp-2 whitespace-normal break-words">{log.details || '—'}</span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-muted/20 border-b">
                              <td colSpan={7} className="px-8 py-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                  <div><span className="font-medium text-muted-foreground">Full Details: </span><span className="break-words">{log.details || '—'}</span></div>
                                  <div><span className="font-medium text-muted-foreground">Resource ID: </span><span className="font-mono text-xs break-all">{log.resource_id || '—'}</span></div>
                                  <div><span className="font-medium text-muted-foreground">User ID: </span><span className="font-mono text-xs break-all">{log.user_id || '—'}</span></div>
                                  <div><span className="font-medium text-muted-foreground">Customer ID: </span><span className="font-mono text-xs break-all">{log.customer_id || '—'}</span></div>
                                  <div><span className="font-medium text-muted-foreground">IP Address: </span>{log.ip_address || '—'}</div>
                                  <div><span className="font-medium text-muted-foreground">Timestamp: </span>{log.created_at}</div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex justify-between items-center px-6 py-4 border-t">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPagination(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
                  disabled={pagination.skip === 0}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.limit }))}
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
